<template>
  <UDashboardPanel id="workflows-detail" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <template #header>
      <UDashboardNavbar :toggle="false" :class="[navbarColorClass, 'transition-colors duration-200']" :ui="{ title: 'flex items-center gap-1.5 font-semibold text-highlighted overflow-visible' }">
        <!--
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
        -->
        <template #title>
          <div v-if="workflow" class="flex items-center gap-2">
            <UIcon v-if="[WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status)" name="i-lucide-circle-dollar-sign" class="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0 hidden sm:block" />
            <div v-if="currentPurchaseOrderType" class="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold shrink-0" :class="isVehicleOrderType(currentPurchaseOrderType) ? 'bg-indigo-50/80 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-300 dark:ring-indigo-700' : 'bg-amber-50/80 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 ring-1 ring-amber-300 dark:ring-amber-700'">
              <UIcon :name="isVehicleOrderType(currentPurchaseOrderType) ? 'i-mdi-car-hatchback' : 'i-mdi-tire'" class="w-4 h-4" />
              <span>{{ isVehicleOrderType(currentPurchaseOrderType) ? t('workflowDetail.purchaseOrderType.vehicle') : t('workflowDetail.purchaseOrderType.tyre') }}</span>
            </div>
            
            <div v-if="workflow.tenant === Tenant.FI && workflow.quotation?.description" class="bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-md flex items-center gap-1.5 min-w-0 border border-blue-200 dark:border-blue-700/40 sm:max-w-none">
              <span class="text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">{{ workflow.quotation?.description }}</span>
            </div>
            
            <UTooltip v-if="workflow.tenant === Tenant.FI && workflow.vehicle" :text="`${workflow.vehicle.manufacturer} ${[getSapLocalizedText(workflow.vehicle.modelDescription, locale) !== '-' ? getSapLocalizedText(workflow.vehicle.modelDescription, locale) : [workflow.vehicle.model, workflow.vehicle.trim].filter(Boolean).join(' '), workflow.vehicle.category, workflow.vehicle.powerKW ? workflow.vehicle.powerKW + 'kW' : '', workflow.vehicle.transmission, workflow.vehicle.homologation].filter(Boolean).join(', ')}`" :delay-duration="300">
              <span class="text-xs sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate sm:max-w-2xl">{{ workflow.vehicle.manufacturer }} {{ [getSapLocalizedText(workflow.vehicle.modelDescription, locale) !== '-' ? getSapLocalizedText(workflow.vehicle.modelDescription, locale) : [workflow.vehicle.model, workflow.vehicle.trim].filter(Boolean).join(' '), workflow.vehicle.category, workflow.vehicle.powerKW ? workflow.vehicle.powerKW + 'kW' : '', workflow.vehicle.transmission, workflow.vehicle.homologation].filter(Boolean).join(', ') }}</span>
            </UTooltip>
            <UTooltip v-else :text="workflow.workflowName" :delay-duration="300">
              <span class="text-xs sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate sm:max-w-2xl">{{ workflow.workflowName }}</span>
            </UTooltip>
          </div>
        </template>
        <template #right>
          <div v-if="workflow" class="flex items-center gap-1.5 ml-auto shrink-0 max-w-full overflow-x-auto sm:overflow-visible">
            <div class="flex items-center gap-1.5">
              <UTooltip :delay-duration="0" :text="workflow?.tenant === Tenant.CZ ? t('workflowDetail.header.objectNumber') : t('workflowDetail.header.purchaseOrderNumber')" v-if="workflow?.tenant === Tenant.CZ ? workflow?.purchaseOrders?.[selectedPurchaseOrderIndex]?.number : workflow?.purchaseOrders?.[selectedPurchaseOrderIndex]?.poNumber">
                <UBadge color="secondary" variant="soft" size="lg" :class="['font-mono font-semibold rounded-full transition-all duration-150', canToggleQuotationView ? 'cursor-pointer' : '', canToggleQuotationView && dataViewMode === 'quotation' ? 'opacity-45 hover:opacity-70' : '', [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-blue-50/75 text-blue-700/90 ring-1 ring-blue-300/50 dark:bg-blue-900/15 dark:text-blue-300/90 dark:ring-blue-700/50' : isVehicleOrderType(currentPurchaseOrderType) ? 'bg-indigo-50/75 text-indigo-700/90 ring-1 ring-indigo-300/50 dark:bg-indigo-900/15 dark:text-indigo-300/90 dark:ring-indigo-700/50' : currentPurchaseOrderType === PurchaseOrder.Tyre ? 'bg-amber-50/75 text-amber-700/90 ring-1 ring-amber-300/50 dark:bg-amber-900/15 dark:text-amber-300/90 dark:ring-amber-700/50' : 'text-secondary/90 ring-1 ring-gray-300/50 dark:bg-gray-200/10 dark:text-gray-200/90 dark:ring-gray-600/50']" @click="canToggleQuotationView && (dataViewMode = 'purchaseOrder')">
                  <UIcon name="i-heroicons-cube" class="w-4 h-4 mr-1"/>
                  {{ workflow?.tenant === Tenant.CZ ? workflow?.purchaseOrders[selectedPurchaseOrderIndex]?.number : workflow?.purchaseOrders[selectedPurchaseOrderIndex]?.poNumber }}
                </UBadge>
              </UTooltip>
              <UTooltip :delay-duration="0" :text="t('workflowDetail.header.updatedVersionTooltip', { version: workflow?.purchaseOrders?.[selectedPurchaseOrderIndex]?.importVersion })" v-if="workflow?.tenant === Tenant.FI && (workflow?.purchaseOrders?.[selectedPurchaseOrderIndex]?.importVersion ?? 1) > 1">
                <UBadge color="warning" variant="soft" size="lg" class="font-mono font-semibold rounded-full bg-amber-50/75 text-amber-700/90 ring-1 ring-amber-300/50 dark:bg-amber-900/15 dark:text-amber-300/90 dark:ring-amber-700/50">
                  <UIcon name="i-heroicons-arrow-path" class="w-3.5 h-3.5 mr-1"/>
                  v{{ workflow?.purchaseOrders?.[selectedPurchaseOrderIndex]?.importVersion }}
                </UBadge>
              </UTooltip>
              <UTooltip :delay-duration="0" :text="canToggleQuotationView ? t('workflowDetail.header.viewQuotationData') : (workflow?.tenant === Tenant.CZ ? t('workflowDetail.header.calculationNumber') : t('workflowDetail.header.quotationNumber'))" v-if="workflow?.quotation?.number && [Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.TyreSupplier, Permission.CustomerCare, Permission.Driver, Permission.HR, Permission.FleetManager, Permission.ClientAccount, Permission.KeyAccountManager, Permission.DeliverySpecialist, Permission.DeliveryManager, Permission.PurchaseSupport].some(p => userStore.hasPermission(p))">
                <UBadge color="secondary" variant="soft" size="lg" :class="['font-mono font-semibold rounded-full transition-all duration-150', canToggleQuotationView ? 'cursor-pointer' : '', canToggleQuotationView && dataViewMode === 'purchaseOrder' ? 'opacity-45 hover:opacity-70' : '', [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-blue-50/75 text-blue-700/90 ring-1 ring-blue-300/50 dark:bg-blue-900/15 dark:text-blue-300/90 dark:ring-blue-700/50' : isVehicleOrderType(currentPurchaseOrderType) ? 'bg-indigo-50/75 text-indigo-700/90 ring-1 ring-indigo-300/50 dark:bg-indigo-900/15 dark:text-indigo-300/90 dark:ring-indigo-700/50' : currentPurchaseOrderType === PurchaseOrder.Tyre ? 'bg-amber-50/75 text-amber-700/90 ring-1 ring-amber-300/50 dark:bg-amber-900/15 dark:text-amber-300/90 dark:ring-amber-700/50' : 'text-secondary/90 ring-1 ring-gray-300/50 dark:bg-gray-200/10 dark:text-gray-200/90 dark:ring-gray-600/50']" @click="canToggleQuotationView && (dataViewMode = 'quotation')">
                  <UIcon name="i-heroicons-calculator" class="w-4 h-4 mr-1 hidden sm:inline-block"/>
                  {{ workflow?.quotation?.number }}
                </UBadge>
              </UTooltip>
              <UTooltip :delay-duration="0" :text="t('workflowDetail.header.updatedVersionTooltip', { version: workflow?.quotation?.importVersion })" v-if="workflow?.tenant === Tenant.FI && (workflow?.quotation?.importVersion ?? 1) > 1">
                <UBadge color="warning" variant="soft" size="lg" class="font-mono font-semibold rounded-full bg-amber-50/75 text-amber-700/90 ring-1 ring-amber-300/50 dark:bg-amber-900/15 dark:text-amber-300/90 dark:ring-amber-700/50">
                  <UIcon name="i-heroicons-arrow-path" class="w-3.5 h-3.5 mr-1"/>
                  v{{ workflow?.quotation?.importVersion }}
                </UBadge>
              </UTooltip>
            </div>
            <div class="flex items-center gap-0.5 rounded-lg border px-1 py-1 shadow-none sm:shadow-sm backdrop-blur-sm transition-all duration-150"  :class="workflowEditStore.isEditMode ? 'bg-white/75 dark:bg-amber-950/30 border-amber-300/70 dark:border-amber-700/70' : [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-white/75 dark:bg-blue-950/20 border-blue-200/80 dark:border-blue-700/60' : isVehicleOrderType(currentPurchaseOrderType) ? 'bg-white/75 dark:bg-indigo-950/20 border-indigo-200/80 dark:border-indigo-700/60' : currentPurchaseOrderType === PurchaseOrder.Tyre ? 'bg-white/75 dark:bg-amber-950/20 border-amber-200/80 dark:border-amber-700/60' : 'bg-white/75 dark:bg-primary-950/20 border-primary-200/80 dark:border-primary-700/60'">
              <div class="flex items-center gap-1">
                <UTooltip :delay-duration="0" :text="pdfTooltip">
                  <UButton
                    color="secondary"
                    variant="soft"
                    icon="i-heroicons-document-arrow-down"
                    size="sm"
                    :class="pdfButtonClasses"
                    :disabled="pdfDownloading"
                    :loading="pdfDownloading"
                    @click="downloadPdf"
                  >
                    {{ t('workflowDetail.header.pdf') }}
                  </UButton>
                </UTooltip>
                <UTooltip v-if="registrationProxyItem && workflow?.tenant === Tenant.FI && [Permission.VehicleSupplier, Permission.TyreSupplier, Permission.PurchaseSupport].some(p => userStore.hasPermission(p))" :delay-duration="0" :text="t('workflowDetail.header.downloadRegistrationProxy')">
                  <UButton
                    color="secondary"
                    variant="soft"
                    icon="i-heroicons-identification"
                    size="sm"
                    :class="pdfButtonClasses"
                    :disabled="registrationProxyDownloading"
                    :loading="registrationProxyDownloading"
                    :aria-label="t('workflowDetail.header.downloadRegistrationProxy')"
                    @click="downloadRegistrationProxy"
                  >
                    {{ t('workflowDetail.header.registrationProxy') }}
                  </UButton>
                </UTooltip>
                <UButton
                  v-if="canReject"
                  color="error"
                  variant="soft"
                  icon="i-heroicons-x-circle"
                  size="sm"
                  class="h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70"
                  @click="isRejectModalOpen = true"
                >
                  {{ t('workflowDetail.header.reject') }}
                </UButton>
                <UButton
                  v-if="canRestart"
                  color="primary"
                  variant="soft"
                  icon="i-heroicons-arrow-path"
                  size="sm"
                  class="h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70 disabled:opacity-55 disabled:cursor-not-allowed disabled:active:scale-100"
                  :disabled="restartLoading"
                  :loading="restartLoading"
                  @click="handleRestart"
                >
                  {{ t('workflowDetail.header.restart') }}
                </UButton>
              </div>
              
              <div class="h-5 w-px bg-gray-300/80 dark:bg-gray-700/80 mx-1.5" v-if="isInternalOrDealer"></div>
              
              <UTooltip v-if="!workflowEditStore.isEditMode" :delay-duration="0" :text="t('workflowDetail.actions.edit')">
                <UButton
                  v-if="isInternalOrDealer"
                  color="primary"
                  variant="soft"
                  icon="i-heroicons-pencil-square"
                  size="sm"
                  class="h-7 w-7 rounded-lg transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70"
                  @click="toggleEditMode"
                />
              </UTooltip>
              <UButton
                v-if="workflowEditStore.isEditMode && isInternalOrDealer"
                color="secondary"
                variant="ghost"
                icon="i-heroicons-x-mark"
                size="sm"
                class="h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70 dark:text-gray-200"
                @click="toggleEditMode"
              >
                {{ t('workflowDetail.actions.cancel') }}
              </UButton>
              <UButton
                v-if="workflowEditStore.isEditMode && isInternalOrDealer"
                color="primary"
                variant="solid"
                icon="i-heroicons-check"
                size="sm"
                class="h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70 text-white"
                @click="saveChanges"
              >
                {{ t('workflowDetail.actions.save') }}
              </UButton>
            </div>
          </div>
        </template>
      </UDashboardNavbar>
    </template>
    
    <template #body>
      <div v-if="loading" class="flex justify-center items-center h-full">
        <UIcon name="i-heroicons-arrow-path" class="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4"/>
      </div>
      
      <div v-else-if="error" class="p-4">
        <UAlert
          title="Error"
          :description="error"
          color="red"
          variant="soft"
          icon="i-heroicons-exclamation-triangle"
        />
      </div>
      
      <div v-else-if="workflow" class="p-4 space-y-6">
        <!--
        <div v-if="workflow.purchaseOrders && workflow.purchaseOrders.length > 1" class="mb-6">
          <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-xs">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
              <div
                v-for="(po, index) in workflow.purchaseOrders"
                :key="index"
                @click="selectedPurchaseOrderIndex = index"
                :class="[
                  'relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 group',
                  selectedPurchaseOrderIndex === index ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20 shadow-md' : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-primary-300 hover:shadow-sm'
                ]"
              >
                <div v-if="selectedPurchaseOrderIndex === index" class="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                  <UIcon name="i-heroicons-check" class="w-3 h-3" />
                </div>

                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2">
                    <UIcon name="i-heroicons-document-text" :class="selectedPurchaseOrderIndex === index ? 'text-primary-600' : 'text-gray-500'" class="w-5 h-5"/>
                    <span class="font-mono text-sm font-semibold text-gray-900 dark:text-white">{{ po.number || `PO-${index + 1}` }}</span>
                  </div>

                  <div v-if="po.supplier" class="text-xs text-gray-600 dark:text-gray-400">
                    {{ po.supplier.name }}
                  </div>

                  <div v-if="isSapOrder(po) && po.supplierInternalOrderNumber" class="text-xs text-gray-500 dark:text-gray-500 font-mono">
                    {{ po.supplierInternalOrderNumber }}
                  </div>

                  <div v-if="po.currentProcessStep" class="mt-1">
                    <UBadge size="xs" :color="selectedPurchaseOrderIndex === index ? 'primary' : 'gray'" variant="soft" class="text-xs">
                      {{ po.currentProcessStep.name || 'In Progress' }}
                    </UBadge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        -->
        
        <div class="mb-3">
          <WorkflowProcessStatus :workflow="workflow" :driver="driver" :driverLoading="driverLoading" :drivers="drivers" :purchaseOrderIndex="selectedPurchaseOrderIndex" :purchaseOrderNumber="selectedPurchaseOrderNumber" @reload="reloadData" />
        </div>
        
        <UTabs v-model="activeTab" :items="filteredTabItems" class="w-full mt-1" color="neutral" :ui="{ list: 'sticky top-0 z-10 bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur rounded-xl border border-gray-300/60 dark:border-gray-700/70 shadow-sm px-1 overflow-x-auto', indicator: 'bg-transparent', trigger: 'rounded-lg px-1.5 py-1.5 sm:px-3 text-gray-700 dark:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-700/50 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-gray-300 dark:data-[state=active]:bg-gray-900 dark:data-[state=active]:text-gray-100 dark:data-[state=active]:ring-gray-600 cursor-pointer transition [&_[data-slot=label]]:hidden [&_[data-slot=label]]:sm:inline' }">
          <template #trailing="{ item }">
            <div v-if="item.value === 'notes'">
              <UBadge class="font-bold rounded-full" size="sm" variant="outline" color="neutral">{{ workflow?.notes?.length }}</UBadge>
            </div>
            <div v-if="item.value === 'documents'">
              <UBadge class="font-bold rounded-full" size="sm" variant="outline" color="neutral">{{ documentsCount }}</UBadge>
            </div>
          </template>
          <template #content="{ item }">
            <div class="h-full overflow-y-auto">
              <div v-if="item.value === 'overview'">
                <WorkflowOverview ref="workflowOverviewRef" :workflow="workflow" :driver="driver" :driverLoading="driverLoading" :driverError="driverError" :drivers="drivers" :contactPerson="contactPerson" :contactPersonLoading="contactPersonLoading" :contactPersonError="contactPersonError" :costCenter="costCenter" :costCenterLoading="costCenterLoading" :costCenterError="costCenterError" :supplierSalesPerson="supplierSalesPerson" :supplierSalesPersonLoading="supplierSalesPersonLoading" :supplierSalesPersonError="supplierSalesPersonError" :supplier="supplier" :supplierLoading="supplierLoading" :supplierError="supplierError" :suppliersByPo="suppliersByPo" :dealer="dealer" :isEditMode="workflowEditStore.isEditMode" :purchaseOrderIndex="selectedPurchaseOrderIndex" :viewQuotationData="dataViewMode === 'quotation' || workflow?.status === 'QuotationApproval' || workflow?.status === 'QuotationRejected'" />
              </div>
              
              <div v-if="item.value === 'additional'">
                <WorkflowAdditionalInformation :workflow="workflow" :purchaseOrderIndex="selectedPurchaseOrderIndex" :viewQuotationData="dataViewMode === 'quotation'" />
              </div>
              
              <div v-if="item.value === 'documents'">
                <WorkflowDocuments :workflow="workflow" @documents-changed="(count) => documentsCount = count" />
              </div>
              
              <div v-if="item.value === 'notes'">
                <WorkflowNotes :workflow="workflow" @reload="reloadData" />
              </div>
              
              <div v-if="item.value === 'history'">
                <EventHistory :external-reference="workflow.externalReference" />
              </div>
            </div>
          </template>
        </UTabs>
        
        <RejectWorkflowModal
          v-model="isRejectModalOpen"
          :loading="rejectLoading"
          :isQuotation="workflow.status === WorkflowStatus.QuotationApproval"
          @confirm-quotation="(reason) => handleReject(true, { reason })"
          @confirm-purchase-order="(initiator) => handleReject(false, { initiator })"
        />
      </div>
    </template>
  </UDashboardPanel>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle'
import { useRoute, useRouter } from 'vue-router'
import { useApiAdapter, useUser } from "@/store"
import { api, PersonPartner, ContactPersonDto, SupplierSalesPersonDto, DriverDto, type CostCenterDto, type OrganizationPartnerDto, type SupplierPartnerDto, BusinessPartnerAddress, type WorkflowDto, type ItemDto, WorkflowStatus, TyreOrderStep, VehicleOrderStep, QuotationStep, DeliveryStep, type LocalFileDto } from '@/api/eOrdersApi'
import { useI18n } from "vue-i18n"
import { Permission } from "@/types/enums/Permission.ts"
import { isSapOrder } from "@/types/discriminators"
import { PurchaseOrder, isVehicleOrderType } from "@/types/enums/PurchaseOrder.ts"
import { showError, showSuccess, showValidationErrors } from "@/utils/utils.ts"
import { deserializeJson } from "@/utils/jsonDeserializer.ts"
import { Tenant } from "@/types/enums/Tenant.ts"
import { getSapLocalizedText } from "@/utils/sapLocalization"
import { transformPatches } from "@/utils/transformedPatches.ts"
import { useWorkflowEdit } from "@/store/workflowEdit"
import jsonpatch from 'fast-json-patch'
import EventHistory from "@/components/workflows/EventHistory.vue"
import WorkflowDocuments from "@/components/workflows/WorkflowDocuments.vue"
import WorkflowAdditionalInformation from "@/components/workflows/WorkflowAdditionalInformation.vue"
import RejectWorkflowModal from "@/components/workflows/RejectWorkflowModal.vue"

type SupplierLike = SupplierPartnerDto | OrganizationPartnerDto;

const { t, locale } = useI18n()

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const error = ref<string | null>(null)

const userStore = useUser()
const apiAdapter = useApiAdapter()
const workflowEditStore = useWorkflowEdit()

const selectedPurchaseOrderIndex = ref(0);
const selectedPurchaseOrderNumber = ref<string | null>(null);
const dataViewMode = ref<'purchaseOrder' | 'quotation'>('purchaseOrder');

const selectedPurchaseOrder = computed(() => {
  if (!workflow.value?.purchaseOrders || !workflow.value.purchaseOrders[selectedPurchaseOrderIndex.value]) {
    return workflow.value?.purchaseOrders?.[0] || null;
  }
  
  return workflow.value.purchaseOrders[selectedPurchaseOrderIndex.value];
});

const currentPurchaseOrderType = computed(() => selectedPurchaseOrder.value?.type ?? null)

const canToggleQuotationView = computed(() => {
  if (workflow.value?.tenant !== Tenant.FI) return false;
  if (!workflow.value?.purchaseOrders?.length) return false;
  if (!isVehicleOrderType(currentPurchaseOrderType.value)) return false;
  
  return [Permission.CustomerCare, Permission.PurchaseSupport, Permission.DeliverySpecialist, Permission.DeliveryManager, Permission.Backoffice, Permission.ClientAccount, Permission.KeyAccountManager].some(p => userStore.hasPermission(p));
});

const pdfButtonBaseClasses = 'h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70'

const pdfButtonClasses = computed(() => {
  if (workflow.value && [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.value.status)) {
    return `${pdfButtonBaseClasses} !text-blue-900 !bg-blue-200/65 hover:!bg-blue-200/85 active:!bg-blue-300/70 dark:!text-blue-100 dark:!bg-blue-700/45 dark:hover:!bg-blue-700/55 dark:active:!bg-blue-700/60`
  }
  
  if (isVehicleOrderType(currentPurchaseOrderType.value)) {
    return `${pdfButtonBaseClasses} !text-indigo-900 !bg-indigo-200/65 hover:!bg-indigo-200/85 active:!bg-indigo-300/70 dark:!text-indigo-100 dark:!bg-indigo-700/45 dark:hover:!bg-indigo-700/55 dark:active:!bg-indigo-700/60`
  }
  
  if (currentPurchaseOrderType.value === PurchaseOrder.Tyre) {
    return `${pdfButtonBaseClasses} !text-amber-900 !bg-amber-200/65 hover:!bg-amber-200/85 active:!bg-amber-300/70 dark:!text-amber-100 dark:!bg-amber-700/45 dark:hover:!bg-amber-700/55 dark:active:!bg-amber-700/60`
  }
  
  return `${pdfButtonBaseClasses} !text-primary-800 !bg-primary-100 hover:!bg-primary-200/70 active:!bg-primary-200 dark:!text-primary-200 dark:!bg-primary-800/40 dark:hover:!bg-primary-800/55 dark:active:!bg-primary-800/60`
})

const navbarColorClass = computed(() => {
  if (workflowEditStore.isEditMode) {
    return 'bg-amber-50 dark:bg-amber-950/40 border-amber-400/60 dark:border-amber-500/40'
  }
  
  if (!workflow.value) return 'border-transparent'
  
  if ([WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.value.status)) {
    return 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/40'
  }
  
  if (isVehicleOrderType(currentPurchaseOrderType.value)) {
    return 'bg-indigo-100 dark:bg-indigo-900/20 border-transparent'
  }
  
  if (currentPurchaseOrderType.value === PurchaseOrder.Tyre) {
    return 'bg-amber-100 dark:bg-amber-900/20 border-transparent'
  }
  
  return 'bg-primary-100 dark:bg-primary-900/30 border-transparent'
})

const workflowOverviewRef = ref();

const isInternalOrDealer = computed(() => [Permission.CustomerCare, Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport, Permission.Dealer, Permission.VehicleSupplier, Permission.TyreSupplier, Permission.SupplierSuperUser].some(permission => userStore.hasPermission(permission)))

const isCustomerOnly = computed(() => [Permission.Driver, Permission.HR, Permission.FleetManager].some(p => userStore.hasPermission(p)) && !isInternalOrDealer.value)

const workflow = ref<WorkflowDto | null>(null)
const driver = ref<DriverDto | null>(null)
const driverLoading = ref(false)
const driverError = ref(false)
const drivers = ref<PersonPartner[]>([])
const contactPerson = ref<ContactPersonDto | null>(null)
const contactPersonLoading = ref(false)
const contactPersonError = ref(false)
const costCenter = ref<CostCenterDto | null>(null)
const costCenterLoading = ref(false)
const costCenterError = ref(false)
const supplierSalesPerson = ref<SupplierSalesPersonDto | null>(null)
const supplierSalesPersonLoading = ref(false)
const supplierSalesPersonError = ref(false)
const supplier = ref<SupplierLike | null>(null)
const supplierLoading = ref(false)
const supplierError = ref(false)
const suppliersByPo = ref<Record<string, SupplierPartnerDto | null>>({})
const dealer = ref<BusinessPartnerAddress | null>(null)
const documentsCount = ref(0)
const contentManagerItems = ref<ItemDto[]>([])
const pdfDownloading = ref(false)
const registrationProxyDownloading = ref(false)

const registrationProxyItem = computed(() =>
  contentManagerItems.value.find(item => item.itemtypeName === 'proxy_for_registration') ?? null
);

const activeContentManagerItem = computed(() => {
  if (!workflow.value || contentManagerItems.value.length === 0) return null;
  const status = workflow.value.status;
  const isQuotationPhase = [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(status);
  const targetItemType = isQuotationPhase || dataViewMode.value === 'quotation' || isCustomerOnly.value ? 'quotation' : 'purchase_order';
  const candidates = contentManagerItems.value.filter(item => item.itemtypeName !== 'proxy_for_registration');
  
  return candidates.find(item => item.itemtypeName === targetItemType) ?? candidates[0] ?? null;
});

const pdfTooltip = computed(() => {
  if (!workflow.value || [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.value.status) || dataViewMode.value === 'quotation' || isCustomerOnly.value) {
    return t('workflowDetail.header.downloadCalculation');
  }
  
  return t('workflowDetail.header.downloadPurchaseOrder');
})

const isRejectModalOpen = ref(false)
const rejectLoading = ref(false)
const restartLoading = ref(false)

usePageTitle(computed(() => workflow.value?.externalReference))

const canReject = computed(() => {
  if (!workflow.value) return false
  if (!userStore.hasPermission(Permission.CustomerCare) && !userStore.hasPermission(Permission.PurchaseSupport)) return false
  
  if (finalStatuses.includes(workflow.value.status)) return false
  
  if (workflow.value.status === WorkflowStatus.QuotationApproval) return true
  
  const purchaseOrder = selectedPurchaseOrder.value
  if (!purchaseOrder) return false
  
  if (purchaseOrder.type === PurchaseOrder.Tyre) {
    return !finalTyreOrderSteps.includes(purchaseOrder.currentProcessStep as TyreOrderStep)
  }
  
  if (isVehicleOrderType(purchaseOrder.type)) {
    if (purchaseOrder.currentProcessStep === VehicleOrderStep.DealerConfirmation) return true
    
    if ([DeliveryStep.Waiting, DeliveryStep.Handover].includes(workflow.value.delivery?.currentProcessStep)) return true
    
    return false
  }
  
  return false
})

const canRestart = computed(() => {
  if (!workflow.value) return false
  if (!userStore.hasPermission(Permission.CustomerCare)) return false
  
  if (workflow.value?.quotation?.currentProcessStep === QuotationStep.Rejection) return true
  
  const purchaseOrder = selectedPurchaseOrder.value
  if (!purchaseOrder) return false
  
  if (purchaseOrder.type === PurchaseOrder.Tyre) {
    return purchaseOrder.currentProcessStep === TyreOrderStep.Rejection
  }
  
  if (isVehicleOrderType(purchaseOrder.type)) {
    return purchaseOrder.currentProcessStep === VehicleOrderStep.DealerRejection || purchaseOrder.currentProcessStep === VehicleOrderStep.DealerRejectionConfirmation
  }
  
  return false
})

const activeTab = computed({
  get() {
    return (route.query.tab as string) || 'overview'
  },
  set(tab) {
    router.push({
      query: {
        ...route.query,
        tab
      }
    })
  }
})

const filteredTabItems = computed(() => {
  return tabItems.value.filter(tabItem => {
    if (tabItem.value === 'additional' && currentPurchaseOrderType.value === PurchaseOrder.Tyre) {
      return false;
    }

    if (!tabItem.restricted) {
      return true
    }
    
    return tabItem.requiredPermissions?.some(permission => userStore.hasPermission(permission)) ?? false
  })
})

const tabItems = computed(() => [
  {
    value: 'overview',
    label: t('workflowDetail.tabs.overview'),
    icon: 'i-heroicons-clipboard-document-list'
  },
  {
    value: 'additional',
    label: t('workflowDetail.tabs.additionalInformation'),
    icon: 'i-lucide-layers'
  },
  {
    value: 'documents',
    label: t('workflowDetail.tabs.documents'),
    icon: 'i-heroicons-folder-open',
    restricted: true,
    requiredPermissions: [Permission.CustomerCare, Permission.VehicleSupplier, Permission.SupplierSuperUser, Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport, Permission.Dealer]
  },
  {
    value: 'notes',
    label: t('workflowDetail.tabs.notes'),
    icon: 'i-heroicons-chat-bubble-left-right',
    restricted: true,
    requiredPermissions: [Permission.Driver, Permission.HR, Permission.CustomerCare, Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport, Permission.Dealer]
  },
  {
    value: 'history',
    label: t('workflowDetail.tabs.eventHistory'),
    icon: 'i-heroicons-clock',
    restricted: true,
    requiredPermissions: [Permission.CustomerCare, Permission.KeyAccountManager, Permission.ClientAccount, Permission.Backoffice, Permission.DeliveryManager, Permission.DeliverySpecialist, Permission.PurchaseSupport]
  },
])

const finalStatuses: WorkflowStatus[] = [
  WorkflowStatus.Completed,
  WorkflowStatus.Archived,
  WorkflowStatus.QuotationRejected,
  WorkflowStatus.OrderRejected
];

const finalTyreOrderSteps: TyreOrderStep[] = [TyreOrderStep.Delivered, TyreOrderStep.Rejection, TyreOrderStep.Rejected];
const finalVehicleOrderSteps: VehicleOrderStep[] = [VehicleOrderStep.Completed, VehicleOrderStep.Rejected];

const isPurchaseOrderFinal = (workflow: WorkflowDto, purchaseOrderIndex: number): boolean => {
  const purchaseOrder = workflow.purchaseOrders?.[purchaseOrderIndex];
  
  if (!purchaseOrder) return false;

  if (purchaseOrder.type === PurchaseOrder.Tyre) {
    return finalTyreOrderSteps.includes(purchaseOrder.currentProcessStep as TyreOrderStep);
  }
  
  if (isVehicleOrderType(purchaseOrder.type)) {
    return finalVehicleOrderSteps.includes(purchaseOrder.currentProcessStep as VehicleOrderStep);
  }
  
  return false;
};

const fetchWorkflowData = async (etag?: string, pollTimeout: number = 5000) => {
  const externalReference = route.params.id as string
  
  if (!externalReference) {
    error.value = 'External Reference not found in URL'
    loading.value = false
    return
  }

  loading.value = true
  error.value = null
  
  try {
    const result = await apiAdapter.invokeRaw(
      requestConfig => api.getWorkflow(externalReference, etag, pollTimeout, requestConfig)
    );
    
    if (result.type === 'not_modified') {
      return;
    }
    
    if (result.type === 'success') {
      const { data, response } = result;

      const deserializedWorkflowData = deserializeJson(data);
      
      workflow.value = deserializedWorkflowData
      workflowEditStore.setEditableWorkflow(deserializedWorkflowData);
      
      if (deserializedWorkflowData.purchaseOrders) {
        const vehicleIndex = deserializedWorkflowData.purchaseOrders.findIndex((po: any) => isVehicleOrderType(po.type));
        if (vehicleIndex !== -1) {
          selectedPurchaseOrderIndex.value = vehicleIndex;
        }
      }
      
      loadDriverData();
      loadContactPersonData();
      loadCostCenterData();
      loadSupplierSalesPersonData();
      await loadContentManagerItems();
      await loadAllDriversData();
      await loadDealerData();
      await loadSupplierData();
      await loadDocumentsCount();
    } else {
      showError('Failed to load workflow data. Please try again later.')
    }
  } catch (err) {
    showError('Network error occurred while fetching workflow data.')
  } finally {
    loading.value = false
  }
}

async function loadDriverData() {
  const driverId = workflow.value?.driverId;
  const externalReference = workflow.value?.externalReference;

  if (!driverId || !externalReference) {
    driver.value = null;
    return;
  }

  driverLoading.value = true;
  driverError.value = false;

  try {
    const driverData = await apiAdapter.invoke<DriverDto>(
        requestConfig => api.driver(externalReference, requestConfig),
        () => {}
    );
    
    if (driverData) {
      driver.value = driverData;
    }
  } catch (e) {
    driverError.value = true;
  } finally {
    driverLoading.value = false;
  }
}

async function loadContactPersonData() {
  const contactPersonId = workflow.value?.contactPersonId;
  const externalReference = workflow.value?.externalReference;
  
  if (!contactPersonId || !externalReference) {
    contactPerson.value = null;
    return;
  }
  
  contactPersonLoading.value = true;
  contactPersonError.value = false;
  
  try {
    const contactPersonData = await apiAdapter.invoke<ContactPersonDto>(
        requestConfig => api.contactPerson(externalReference, requestConfig),
        () => {}
    );
    
    if (contactPersonData) {
      contactPerson.value = contactPersonData;
    }
  } catch (e) {
    contactPersonError.value = true;
  } finally {
    contactPersonLoading.value = false;
  }
}

async function loadCostCenterData() {
  const costCenterId = workflow.value?.costCenterId;
  const externalReference = workflow.value?.externalReference;
  
  if (!costCenterId || !externalReference) {
    costCenterError.value = false;
    return;
  }
  
  costCenterLoading.value = true;
  costCenterError.value = false;
  
  try {
    const costCenterData = await apiAdapter.invoke<CostCenterDto>(
        requestConfig => api.costCenter(externalReference, requestConfig),
        () => {}
    );
    
    if (costCenterData) {
      costCenter.value = costCenterData;
    }
  } catch (e) {
    costCenterError.value = true;
  } finally {
    costCenterLoading.value = false;
  }
}

async function loadSupplierSalesPersonData() {
  const supplierSalesPersonId = workflow.value?.supplierSalesPersonId;
  const externalReference = workflow.value?.externalReference;
  
  if (!supplierSalesPersonId || !externalReference) {
    supplierSalesPerson.value = null;
    return;
  }
  
  supplierSalesPersonLoading.value = true;
  supplierSalesPersonError.value = false;
  
  try {
    const supplierSalesPersonData = await apiAdapter.invoke<SupplierSalesPersonDto>(
        requestConfig => api.supplierSalesperson(externalReference, requestConfig),
        () => {}
    );
    
    if (supplierSalesPersonData) {
      supplierSalesPerson.value = supplierSalesPersonData;
    }
  } catch (e) {
    supplierSalesPersonError.value = true;
  } finally {
    supplierSalesPersonLoading.value = false;
  }
}

async function loadAllDriversData() {
  if (!workflow.value?.externalReference) return

  if (drivers.value.length > 0) return

  try {
    const driversData = await apiAdapter.invoke<PersonPartner[]>(
        requestConfig => api.clientDrivers(workflow.value!.externalReference, requestConfig),
        () => {}
    );
    
    if (driversData) {
      drivers.value = driversData
    }
  } catch (e) {
    
  }
}

async function loadDealerData() {
  const externalReference = workflow.value?.externalReference;
  if (!externalReference) return;

  try {
    const dealerData = await apiAdapter.invoke(
      requestConfig => api.getWorkflowsDealerAddress(externalReference, requestConfig),
      () => {}
    );

    if (dealerData) {
      dealer.value = dealerData;
    }
  } catch (e) {
    showError('Failed to load dealer');
  }
}

async function loadSupplierData() {
  const externalReference = workflow.value?.externalReference;
  if (!externalReference) {
    supplier.value = null;
    suppliersByPo.value = {};
    supplierError.value = false;
    return;
  }
  
  const isFi = workflow.value?.tenant === Tenant.FI;
  const allPos = workflow.value?.purchaseOrders ?? [];
  const posWithNumber = allPos.filter((po: any) => po?.number) as Array<{ number: string }>;
  const hasDealer = !!workflow.value?.quotation?.dealerId;
  
  if (isFi && posWithNumber.length > 0) {
    supplierLoading.value = true;
    supplierError.value = false;
    
    try {
      const results = await Promise.all(
        posWithNumber.map(po =>
          apiAdapter.invoke<SupplierPartnerDto>(
            requestConfig => api.supplier(externalReference, po.number, requestConfig),
            () => {}
          ).catch(() => undefined)
        )
      );
      
      const map: Record<string, SupplierPartnerDto | null> = {};
      
      posWithNumber.forEach((po, i) => {
        map[po.number] = results[i] ?? null;
      });
      
      suppliersByPo.value = map;
      supplier.value = null;
    } finally {
      supplierLoading.value = false;
    }
    
    return;
  }
  
  const purchaseOrderNumber = selectedPurchaseOrder.value?.number;
  
  if (!purchaseOrderNumber && !hasDealer) {
    supplier.value = null;
    suppliersByPo.value = {};
    supplierError.value = false;
    return;
  }
  
  supplierLoading.value = true;
  supplierError.value = false;
  
  try {
    const supplierData = await apiAdapter.invoke<SupplierLike>(
      requestConfig => purchaseOrderNumber
        ? api.supplier(externalReference, purchaseOrderNumber, requestConfig)
        : api.dealer(externalReference, requestConfig),
      () => {}
    );
    
    supplier.value = supplierData ?? null;
    suppliersByPo.value = {};
  } catch (e) {
    supplierError.value = true;
  } finally {
    supplierLoading.value = false;
  }
}

async function loadDocumentsCount() {
  const externalReference = workflow.value?.externalReference;
  if (!externalReference) return;

  try {
    const documents = await apiAdapter.invoke<LocalFileDto[]>(
      requestConfig => api.localFilesGetfiles(externalReference, requestConfig),
      () => {}
    );
    
    const localCount = documents?.length ?? 0;
    const cmCount = contentManagerItems.value.filter(item => item.itemtypeName === 'vehicle_delivery_form').length;
    
    documentsCount.value = localCount + cmCount;
  } catch (e) {}
}

async function loadContentManagerItems() {
  const externalReference = workflow.value?.externalReference;
  
  if (!externalReference) return;
  
  try {
    const items = await apiAdapter.invoke<ItemDto[]>(
      requestConfig => api.getWorkflowContentManager(externalReference, requestConfig),
      () => {}
    );
    
    if (items) {
      contentManagerItems.value = items;
    }
  } catch (e) {
    
  }
}

async function downloadContentManagerItem(item: ItemDto | null) {
  const externalReference = workflow.value?.externalReference;
  
  if (!externalReference) return;
  
  if (!item) {
    showError(t('workflowDetail.header.pdfNotFound'));
    return;
  }
  
  let url: string | null = null;
  
  try {
    const result = await apiAdapter.invokeRaw(
      (config) => api.downloadWorkflowContentManager(externalReference, { pid: item.pid }, {
        ...config,
        responseType: 'blob',
      }) as any,
      t('workflowDetail.header.pdfDownloadError')
    );
    
    if (result?.type === 'success' && result.response) {
      const blob = result.response.data as Blob;
      url = window.URL.createObjectURL(blob);
      
      const contentDisposition = result.response.headers?.['content-disposition'] as string | undefined;
      const fileName = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] ?? item.fileName ?? `${item.pid}.pdf`;
      const link = document.createElement('a');
      
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } finally {
    if (url) window.URL.revokeObjectURL(url);
  }
}

async function downloadPdf() {
  pdfDownloading.value = true;
  
  try {
    await downloadContentManagerItem(activeContentManagerItem.value);
  } finally {
    pdfDownloading.value = false;
  }
}

async function downloadRegistrationProxy() {
  registrationProxyDownloading.value = true;
  
  try {
    await downloadContentManagerItem(registrationProxyItem.value);
  } finally {
    registrationProxyDownloading.value = false;
  }
}

const toggleEditMode = () => {
  if (workflowEditStore.isEditMode) {
    workflowEditStore.setEditableWorkflow(workflow.value)
  }
  
  workflowEditStore.setEditMode(!workflowEditStore.isEditMode)
}

const saveChanges = async () => {
  if (!workflowOverviewRef.value?.validateAllFields()) {
    return;
  }
  
  workflowEditStore.setEditMode(false);
  
  try {
    const mainPatch = jsonpatch.compare(
      {
        quotation: {
          initialQuotationNumber: workflow.value?.quotation?.initialQuotationNumber
        },
        delivery: {
          handoverDate: workflow.value?.delivery?.handoverDate,
          handoverMileage: workflow.value?.delivery?.handoverMileage,
          arrivedAtDealer: workflow.value?.delivery?.arrivedAtDealer,
          licensePlate: workflow.value?.delivery?.licensePlate,
          co2: workflow.value?.delivery?.cO2
        },
        purchaseOrders: [{
          dealerExpectedDeliveryDate: workflow.value?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.dealerExpectedDeliveryDate,
          supplierInternalOrderNumber: workflow.value?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.supplierInternalOrderNumber,
          contactEmailForPO: workflow.value?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.contactEmailForPO
        }],
        driverId: workflow.value?.driverId
      },
      {
        quotation: {
          initialQuotationNumber: workflowEditStore.editableWorkflow?.quotation?.initialQuotationNumber
        },
        delivery: {
          handoverDate: workflowEditStore.editableWorkflow?.delivery?.handoverDate,
          handoverMileage: workflowEditStore.editableWorkflow?.delivery?.handoverMileage,
          arrivedAtDealer: workflowEditStore.editableWorkflow?.delivery?.arrivedAtDealer,
          licensePlate: workflowEditStore.editableWorkflow?.delivery?.licensePlate,
          co2: workflowEditStore.editableWorkflow?.delivery?.cO2
        },
        purchaseOrders: [{
          dealerExpectedDeliveryDate: workflowEditStore.editableWorkflow?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.dealerExpectedDeliveryDate,
          supplierInternalOrderNumber: workflowEditStore.editableWorkflow?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.supplierInternalOrderNumber,
          contactEmailForPO: workflowEditStore.editableWorkflow?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.contactEmailForPO
        }],
        driverId: workflowEditStore.editableWorkflow?.driverId
      }
    ).map(operation => {
      if (operation.path.startsWith('/purchaseOrders/0/')) {
        return {
          ...operation,
          path: operation.path.replace('/purchaseOrders/0/', `/purchaseOrders/${selectedPurchaseOrderNumber.value}/`)
        };
      }
      return operation;
    });

    const extendedPatch = jsonpatch.compare(
      {
        replacedCar: {
          licensePlate: workflow.value?.replacedCar?.licensePlate
        },
      },
      {
        replacedCar: {
          licensePlate: workflowEditStore.editableWorkflow?.replacedCar?.licensePlate
        },
      }
    );

    const hasEmailChange = mainPatch.some((p: any) => typeof p.path === 'string' && p.path.startsWith(`/purchaseOrders/${selectedPurchaseOrderNumber.value}/contactEmailForPO`));

    let transformedMainPatch = mainPatch;
    if (hasEmailChange) {
      const filtered = mainPatch.filter((p: any) => !(typeof p.path === 'string' && p.path.startsWith(`/purchaseOrders/${selectedPurchaseOrderNumber.value}/contactEmailForPO`)));
      
      const replacement = {
        op: 'replace',
        path: `/PurchaseOrders/${selectedPurchaseOrderNumber.value}/ContactEmailForPO`,
        value: workflowEditStore.editableWorkflow?.purchaseOrders?.[selectedPurchaseOrderIndex.value]?.contactEmailForPO ?? []
      } as any;
      
      transformedMainPatch = [...filtered, replacement];
    }

    const currentMode = workflow.value?.quotation?.requestedDeliveryDateScenario?.mode ?? null;
    const currentDate = workflow.value?.quotation?.requestedDeliveryDateScenario?.date ?? null;
    const requestedMode = workflowEditStore.editableWorkflow?.quotation?.requestedDeliveryDateScenario?.mode ?? null;
    const requestedDateStr = workflowEditStore.editableWorkflow?.quotation?.requestedDeliveryDateScenario?.date ?? null;
    
    const extendedDeliveryPatch: any[] = [];
    
    if (requestedMode) {
      let needPatch = false;
      let newDelivery: any = null;
      
      switch (requestedMode) {
        case 'LE':
        case 'GE':
        case 'EQ':
          if (currentMode !== requestedMode || currentDate !== requestedDateStr) {
            needPatch = true;
            newDelivery = {
              mode: requestedMode,
              date: requestedDateStr
            };
          }
          break;
        case 'ASAP':
          if (currentMode !== 'ASAP') {
            needPatch = true;
            newDelivery = {
              mode: 'ASAP'
            };
          } else if (currentDate !== null) {
            needPatch = true;
            newDelivery = {
              mode: 'ASAP'
            };
          }
          break;
        default:
          break;
      }
      if (needPatch && newDelivery) {
        extendedDeliveryPatch.push({
          op: 'replace',
          path: '/quotation/requestedDeliveryDateScenario',
          value: newDelivery,
        });
      }
    }
    
    const combinedPatch = [
      ...transformedMainPatch,
      ...transformPatches(extendedPatch),
      ...extendedDeliveryPatch,
    ];
    
    if (combinedPatch.length === 0 || !workflow.value?.externalReference) {
      return;
    }

    const patchWorkflow = await apiAdapter.invoke<WorkflowDto>(
      requestConfig => api.patchWorkflow(workflow.value!.externalReference, combinedPatch, requestConfig),
      (error) => showValidationErrors(error, t)
    );
    
    if (patchWorkflow) {
      await reloadData();
      showSuccess(t('notification.changesSaveSuccessfully'))
    }
  } catch (err) {
    showError(t('notification.changesSaveFailed'))
  }
}

const handleReject = async (isQuotation: boolean, payload: any) => {
  rejectLoading.value = true
  
  try {
    const result = await apiAdapter.invokeRaw<WorkflowDto>(
      requestConfig => isQuotation ? api.workflowQuotationRejected(workflow.value!.externalReference!, { reason: payload.reason }, requestConfig) : api.workflowPurchaseorderReject(workflow.value!.externalReference!, selectedPurchaseOrderNumber.value!, { initiator: payload.initiator }, requestConfig),
      (error) => showValidationErrors(error, t)
    )
    
    if (result?.type === 'success') {
      isRejectModalOpen.value = false
      await reloadData(result.data, result.response.headers['etag'])
      showSuccess(t(isQuotation ? 'notification.quotationRejectedSuccessfully' : 'notification.orderRejectedSuccessfully'))
    }
  } catch (err) {
    showError(t('notification.rejectFailed'))
  } finally {
    rejectLoading.value = false
  }
}

const handleRestart = async () => {
  restartLoading.value = true
  
  const isQuotation = workflow.value?.quotation?.currentProcessStep === QuotationStep.Rejection
  
  try {
    const result = await apiAdapter.invokeRaw<WorkflowDto>(
      requestConfig => isQuotation ? api.workflowQuotationRestart(workflow.value!.externalReference!, requestConfig) : api.workflowPurchaseorderRestart(workflow.value!.externalReference!, selectedPurchaseOrderNumber.value!, requestConfig),
      (error) => showValidationErrors(error, t)
    )
    
    if (result?.type === 'success') {
      await reloadData(result.data, result.response.headers['etag'])
      showSuccess(t(isQuotation ? 'notification.quotationRestartedSuccessfully' : 'notification.orderRestartedSuccessfully'))
    }
  } catch (err) {
    showError(t('notification.restartFailed'))
  } finally {
    restartLoading.value = false
  }
}

const reloadData = async (workflowData?: WorkflowDto, etag?: string) => {
  loading.value = true
  
  if (workflowData) {
    const deserializedWorkflowData = deserializeJson(workflowData);
    
    workflow.value = deserializedWorkflowData;
    workflowEditStore.setEditableWorkflow(deserializedWorkflowData);
    
    loadDriverData();
    loadContactPersonData();
    loadCostCenterData();
    loadSupplierSalesPersonData();
    await loadContentManagerItems();
    await loadAllDriversData();
    await loadDealerData();
    await loadDocumentsCount();
    
    loading.value = false;
    
    if (etag) {
      const isFinalStatus = finalStatuses.includes(deserializedWorkflowData.status) || isPurchaseOrderFinal(deserializedWorkflowData, selectedPurchaseOrderIndex.value);
      const pollTimeout = isFinalStatus ? 2000 : 5000;

      fetchWorkflowData(etag, pollTimeout);
    }
  } else {
    await fetchWorkflowData();
  }
}

onMounted(async () => {
  await fetchWorkflowData()
})

watch(() => route.params.id, async (newId, oldId) => {
  if (newId !== oldId) {
    selectedPurchaseOrderIndex.value = 0
    await fetchWorkflowData()
  }
})

watch(() => workflow.value?.purchaseOrders, (newPurchaseOrders) => {
  if (newPurchaseOrders && selectedPurchaseOrderIndex.value >= newPurchaseOrders.length) {
    selectedPurchaseOrderIndex.value = 0
  }
}, { deep: true })

watch(selectedPurchaseOrder, (newPurchaseOrder, oldPurchaseOrder) => {
  selectedPurchaseOrderNumber.value = newPurchaseOrder?.number || null;
  dataViewMode.value = 'purchaseOrder';
  
  if (!oldPurchaseOrder?.number || !newPurchaseOrder?.number) return;
  if (newPurchaseOrder.number === oldPurchaseOrder.number) return;
  if (workflow.value?.tenant === Tenant.FI && newPurchaseOrder.number in suppliersByPo.value) return;
  
  loadSupplierData();
}, { immediate: true })
</script>